/**
 * Currency Configuration
 * Supported currencies and country mappings
 */

import type { CurrencyConfig } from '@/types/subscription';

export const SUPPORTED_CURRENCIES: Record<string, CurrencyConfig> = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.92 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.79 },
  MYR: { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', rate: 4.47 },
  SGD: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', rate: 1.34 },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', rate: 1.53 },
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', rate: 1.36 },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', rate: 149.5 },
  CNY: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', rate: 7.24 },
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 83.12 },
  KRW: { code: 'KRW', symbol: '₩', name: 'South Korean Won', rate: 1320 },
  THB: { code: 'THB', symbol: '฿', name: 'Thai Baht', rate: 35.8 },
  PHP: { code: 'PHP', symbol: '₱', name: 'Philippine Peso', rate: 56.2 },
  IDR: { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', rate: 15650 },
  VND: { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', rate: 24500 },
};

export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  GB: 'GBP',
  UK: 'GBP',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  PT: 'EUR',
  MY: 'MYR',
  SG: 'SGD',
  AU: 'AUD',
  CA: 'CAD',
  JP: 'JPY',
  CN: 'CNY',
  IN: 'INR',
  KR: 'KRW',
  TH: 'THB',
  PH: 'PHP',
  ID: 'IDR',
  VN: 'VND',
};

/**
 * Get currency for a country code
 */
export function getCurrencyForCountry(countryCode: string): string {
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || 'USD';
}

/**
 * Format price in a currency
 */
export function formatPrice(
  amountUSD: number,
  currencyCode: string
): { amount: number; formatted: string } {
  const currency = SUPPORTED_CURRENCIES[currencyCode] || SUPPORTED_CURRENCIES.USD;
  const amount = Math.round(amountUSD * currency.rate * 100) / 100;

  // Format based on currency
  let formatted: string;
  if (currency.code === 'JPY' || currency.code === 'KRW' || currency.code === 'VND' || currency.code === 'IDR') {
    // No decimals for these currencies
    formatted = `${currency.symbol}${Math.round(amount).toLocaleString()}`;
  } else {
    formatted = `${currency.symbol}${amount.toFixed(2)}`;
  }

  return { amount, formatted };
}

/**
 * Fetch live exchange rates (placeholder - implement with real API)
 */
export async function fetchExchangeRates(): Promise<Record<string, number>> {
  // In production, fetch from an API like Open Exchange Rates
  // For now, return the static rates
  const rates: Record<string, number> = {};
  Object.entries(SUPPORTED_CURRENCIES).forEach(([code, config]) => {
    rates[code] = config.rate;
  });
  return rates;
}
